
"use client"
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export const FundraisingWorkflowVisual = () => {
    const [activeStep, setActiveStep] = useState(0);

    // Auto-cycle through steps every 5 seconds
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % 6);
        }, 5000); // Slightly slower for better viewing
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="w-full max-w-[500px] aspect-[4/3] bg-[#ffffff] rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.06)] border border-gray-100 relative overflow-hidden flex flex-col font-jakarta group ring-1 ring-gray-50/50">

            {/* Window Chrome / Header - Pure White with subtle border */}
            <div className="h-10 border-b border-gray-50 bg-[#ffffff] flex items-center px-4 justify-between shrink-0 z-20">
                <div className="flex gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57] border border-[#e0443e]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e] border border-[#d89e24]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#28c840] border border-[#1aab29]/30" />
                </div>
                <div className="flex items-center gap-2">
                    <div className="px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100">
                        <span className="text-[9px] font-semibold text-gray-400 tracking-wide uppercase">Deckwale AI</span>
                    </div>
                </div>
                <div className="w-12 flex justify-end">
                    <svg className="w-3 h-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></svg>
                </div>
            </div>

            {/* Main Content Area - Animate between states */}
            <div className="flex-1 relative overflow-hidden bg-[#ffffff] p-6 lg:p-8">
                <AnimatePresence mode="wait">
                    {activeStep === 0 && <Step1_Details key="step1" />}
                    {activeStep === 1 && <Step2_Context key="step2" />}
                    {activeStep === 2 && <Step3_QA key="step3" />}
                    {activeStep === 3 && <Step4_Outline key="step4" />}
                    {activeStep === 4 && <Step5_Theme key="step5" />}
                    {activeStep === 5 && <Step6_Editor key="step6" />}
                </AnimatePresence>

                {/* Progressive Step Indicator - Ultra minimal */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-50 flex">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex-1 relative h-full">
                            {activeStep === i && (
                                <motion.div
                                    className="absolute inset-0 bg-[#2563eb]"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 5, ease: "linear" }}
                                />
                            )}
                            {activeStep > i && <div className="absolute inset-0 bg-[#2563eb] opacity-30" />}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- Step Components ---

// Step 1: Share basic company details
const Step1_Details = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col gap-6 h-full pt-2"
    >
        <div className="flex flex-col gap-1">
            <div className="text-xs font-bold text-gray-900 flex items-center gap-2">
                <span className="w-5 h-5 rounded bg-blue-50 flex items-center justify-center text-blue-600 text-[10px]">1</span>
                Company Basics
            </div>
            <div className="text-[10px] text-gray-400 pl-7">Tell us about your startup</div>
        </div>

        <div className="space-y-4 pl-1">
            <div className="group">
                <label className="text-[9px] font-bold text-gray-400 mb-1.5 block uppercase tracking-wider">Industry Sector</label>
                <div className="w-full h-11 border border-gray-100 rounded-xl flex items-center px-3 gap-3 text-xs text-gray-800 shadow-[0_2px_8px_rgba(0,0,0,0.02)] bg-[#ffffff] group-hover:border-blue-200 group-hover:shadow-[0_4px_12px_rgba(37,99,235,0.05)] transition-all">
                    <div className="p-1 rounded bg-gray-50 text-gray-400"><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg></div>
                    Fintech SaaS
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.4)]" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="group">
                    <label className="text-[9px] font-bold text-gray-400 mb-1.5 block uppercase tracking-wider">Stage</label>
                    <div className="w-full h-11 border border-gray-100 rounded-xl flex items-center px-3 gap-3 text-xs text-gray-800 shadow-[0_2px_8px_rgba(0,0,0,0.02)] bg-[#ffffff] group-hover:border-blue-200 transition-all">
                        <div className="p-1 rounded bg-gray-50 text-gray-400"><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg></div>
                        Seed
                    </div>
                </div>
                <div className="group">
                    <label className="text-[9px] font-bold text-gray-400 mb-1.5 block uppercase tracking-wider">Website</label>
                    <div className="w-full h-11 border border-gray-100 rounded-xl flex items-center px-3 gap-3 text-xs text-gray-800 shadow-[0_2px_8px_rgba(0,0,0,0.02)] bg-[#ffffff] group-hover:border-blue-200 transition-all">
                        <div className="p-1 rounded bg-gray-50 text-gray-400"><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg></div>
                        acme.com
                    </div>
                </div>
            </div>
        </div>
    </motion.div>
);

// Step 2: Provide core business context
const Step2_Context = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full items-center justify-center p-4 relative"
    >
        <div className="w-full h-44 border border-dashed border-gray-200 rounded-2xl bg-[#ffffff] flex flex-col items-center justify-center gap-5 relative overflow-hidden group hover:border-blue-300 transition-colors shadow-sm">
            {/* Background Grid Pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:16px_16px]" />

            <div className="w-14 h-14 rounded-full bg-blue-50/50 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform relative z-10">
                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            </div>
            <div className="text-center relative z-10">
                <div className="text-xs font-bold text-gray-800">Upload Pitch Materials</div>
                <div className="text-[10px] text-gray-400 mt-1.5 bg-white border border-gray-100 px-2 py-0.5 rounded-full inline-block shadow-sm">PDF, DOCX, or URL</div>
            </div>

            {/* Uploading File Processing Animation */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.5 }}
                className="absolute inset-4 bg-[#ffffff] rounded-xl shadow-lg border border-gray-100 flex items-center gap-4 px-4 z-20"
            >
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center shrink-0 border border-red-100">
                    <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between text-[10px] mb-1.5">
                        <span className="font-bold text-gray-800 truncate">Pitch_Draft_v1.pdf</span>
                        <span className="text-blue-600 font-medium">Processing...</span>
                    </div>
                    <div className="h-1.5 bg-gray-50 rounded-full overflow-hidden w-full">
                        <motion.div className="h-full bg-blue-600 rounded-full" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 1.5, ease: "easeInOut" }} />
                    </div>
                </div>
            </motion.div>
        </div>
    </motion.div>
);

// Step 3: Complete Question & Answer
const Step3_QA = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col gap-5 h-full pt-4 px-2"
    >
        {/* AI Question */}
        <div className="flex gap-4">
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white shrink-0 shadow-lg shadow-blue-200 ring-2 ring-white z-10">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            </div>
            <div className="flex-1 pt-1">
                <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] font-bold text-gray-800">Deckwale AI</span>
                    <span className="text-[9px] text-gray-300">Just now</span>
                </div>
                <div className="bg-[#ffffff] border border-gray-100 rounded-2xl rounded-tl-sm p-3.5 shadow-[0_2px_15px_rgba(0,0,0,0.04)] relative group">
                    <p className="text-[11px] text-gray-600 leading-relaxed font-medium">What is your <span className="text-blue-600 font-bold bg-blue-50 px-1 rounded">unfair advantage</span> over these competitors?</p>
                </div>
            </div>
        </div>

        {/* User Answer (Typing animation) */}
        <motion.div
            initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}
            className="flex gap-4 flex-row-reverse"
        >
            <div className="w-8 h-8 rounded-full bg-gray-100 border border-white shadow-sm shrink-0 overflow-hidden flex items-center justify-center">
                <div className="text-[9px] font-bold text-gray-500">YO</div>
            </div>
            <div className="flex-1 text-right pt-1">
                <div className="flex items-center gap-2 mb-1.5 justify-end">
                    <span className="text-[9px] text-gray-300">You</span>
                </div>
                <div className="bg-gray-900 text-white rounded-2xl rounded-tr-sm p-4 shadow-xl inline-block text-left relative">
                    {/* Tail */}
                    <p className="text-[11px] leading-relaxed font-medium">
                        Our patent-pending AI reduces costs by 40% vs legacy tools...
                        <motion.span
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{ repeat: Infinity, duration: 0.8 }}
                            className="inline-block w-0.5 h-3 bg-blue-400 ml-1 align-middle rounded-full"
                        />
                    </p>
                </div>
            </div>
        </motion.div>
    </motion.div>
);

// Step 4: Outline
const Step4_Outline = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full bg-[#ffffff] "
    >
        <div className="flex items-center justify-between mb-5">
            <div className="text-xs font-bold text-gray-900 border-b border-gray-100 pb-1">Generated Structure</div>
            <div className="text-[9px] px-2.5 py-1 bg-gray-50 text-gray-600 rounded-full font-bold border border-gray-100">12 Slides</div>
        </div>

        <div className="space-y-3">
            {[
                { title: "Problem Statement", time: "2 min", active: false },
                { title: "The Solution", time: "3 min", active: true },
                { title: "Market Size", time: "1 min", active: false },
                { title: "Business Model", time: "2 min", active: false }
            ].map((item, i) => (
                <motion.div
                    key={i}
                    initial={{ x: -15, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.12 }}
                    className={`p-3.5 rounded-xl border flex items-center gap-3 transition-all duration-300 bg-[#ffffff] ${item.active ? 'border-blue-500/30 shadow-[0_4px_12px_rgba(37,99,235,0.08)] translate-x-1 ring-1 ring-blue-500/10' : 'border-gray-50 hover:border-gray-200'}`}
                >
                    <div className="text-gray-300 cursor-grab">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" /></svg>
                    </div>
                    <div>
                        <div className={`text-xs font-bold leading-none ${item.active ? 'text-blue-600' : 'text-gray-600'}`}>{item.title}</div>
                        {item.active && <div className="text-[9px] text-gray-400 mt-1 font-medium">Focusing on clarity & impact</div>}
                    </div>

                    <div className="ml-auto text-[10px] text-gray-300 font-medium font-mono">{item.time}</div>
                </motion.div>
            ))}
        </div>
    </motion.div>
);

// Step 5: Themes - Enhanced
const Step5_Theme = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full gap-5 bg-[#ffffff]"
    >
        <div className="text-xs font-bold text-gray-900 border-b border-gray-100 pb-2">Customize Brand</div>

        {/* Brand Kit Panel */}
        <div className="grid grid-cols-2 gap-5 h-full">
            {/* Left: Controls */}
            <div className="space-y-4">

                {/* Logo Upload - Clean White */}
                <div className="space-y-1.5">
                    <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Logo</label>
                    <div className="w-full h-12 border border-gray-100 rounded-xl flex items-center justify-center bg-[#ffffff] shadow-[0_2px_8px_rgba(0,0,0,0.03)] gap-2 hover:border-blue-200 cursor-pointer transition-colors group">
                        <div className="w-7 h-7 rounded-lg bg-black flex items-center justify-center text-white font-bold text-[10px] shadow-lg group-hover:scale-110 transition-transform">A</div>
                        <span className="text-[10px] font-bold text-gray-700">Acme Inc.</span>
                    </div>
                </div>

                {/* Typography - Clean White */}
                <div className="space-y-1.5">
                    <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Font Family</label>
                    <div className="w-full h-10 border border-gray-100 rounded-xl flex items-center px-3 justify-between bg-[#ffffff] shadow-[0_2px_8px_rgba(0,0,0,0.03)] hover:border-blue-200 transition-colors">
                        <span className="text-[10px] font-bold text-gray-800">Inter Tight</span>
                        <svg className="w-3 h-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </div>
                </div>

                {/* Colors */}
                <div className="space-y-2">
                    <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Primary Color</label>
                    <div className="flex gap-2.5">
                        {['#1e293b', '#2563eb', '#0ea5e9'].map((color, i) => (
                            <motion.div
                                key={i}
                                initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5 + i * 0.1 }}
                                className="w-8 h-8 rounded-full border-[3px] border-white shadow-md ring-1 ring-gray-100 cursor-pointer relative"
                                style={{ backgroundColor: color }}
                                whileHover={{ scale: 1.15, zIndex: 10 }}
                            >
                                {i === 1 && <motion.div layoutId="selectedColor" className="absolute inset-0 ring-2 ring-blue-500 rounded-full" />}
                            </motion.div>
                        ))}
                        {/* Color Picker Simulation */}
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 border border-white shadow-md ring-1 ring-gray-100 flex items-center justify-center cursor-pointer">
                            <div className="w-2 h-2 bg-white rounded-full shadow-sm" />
                        </div>
                    </div>
                </div>

            </div>

            {/* Right: Preview Card */}
            <div className="relative pt-2">
                <div className="absolute inset-0 bg-gray-50 rounded-xl transform rotate-3 scale-95 origin-bottom-right" />
                <motion.div
                    className="absolute inset-0 bg-[#ffffff] rounded-xl shadow-[0_10px_30px_-5px_rgba(0,0,0,0.08)] border border-gray-100 p-4 flex flex-col items-center justify-between overflow-hidden"
                    initial={{ y: 15, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                >
                    {/* Preview Slide Content */}
                    <div className="w-full flex justify-between items-center mb-2">
                        <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 bg-black rounded flex items-center justify-center text-[8px] text-white font-bold">A</div>
                            <div className="w-10 h-1 bg-gray-100 rounded-full" />
                        </div>
                    </div>
                    <div className="text-center space-y-2 w-full">
                        <div className="text-[13px] font-extrabold text-gray-900 leading-tight">Reimagining <br /> Finance</div>
                        <div className="w-8 h-0.5 bg-blue-600 mx-auto rounded-full" />
                        <div className="flex items-center justify-center gap-1 mt-2">
                            <div className="w-full h-8 bg-gray-50 rounded border border-gray-50" />
                        </div>
                    </div>

                    {/* Applying Brush Animation - Cleaner */}
                    <motion.div
                        className="absolute -bottom-6 -right-6 text-blue-500 opacity-10"
                        animate={{ rotate: [0, 5, 0], scale: [1, 1.05, 1] }}
                        transition={{ repeat: Infinity, duration: 3 }}
                    >
                        <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17.93c-3.95.49-7-2.61-7-7 0-.55.45-1 1-1s1 .45 1 1c0 2.38 1.19 3.47 3 3.87V19.93zm0-3.93c-2-.38-3-1.62-3-3.87 0-.55-.45-1-1-1s-1 .45-1 1c0 3.31 1.69 5.29 5 5.79V16zM12 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z" /></svg>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    </motion.div>
);

// Step 6: Editor (Final Result)
const Step6_Editor = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full relative"
    >
        {/* Editor Toolbar - Clean White */}
        <div className="h-9 border-b border-gray-100 flex items-center justify-between mb-2 px-1">
            <div className="flex gap-1.5">
                <div className="w-6 h-6 rounded-md hover:bg-gray-50 flex items-center justify-center cursor-pointer border border-transparent hover:border-gray-100 transition-colors"><svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg></div>
                <div className="w-6 h-6 rounded-md hover:bg-gray-50 flex items-center justify-center cursor-pointer border border-transparent hover:border-gray-100 transition-colors"><svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg></div>
                <div className="w-6 h-6 rounded-md hover:bg-gray-50 flex items-center justify-center cursor-pointer border border-transparent hover:border-gray-100 transition-colors"><svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>
            </div>
            <div className="flex items-center gap-2 bg-gray-50 px-2.5 py-1 rounded-full border border-gray-100">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                <span className="text-[9px] font-bold text-gray-600 uppercase tracking-wide">AI Editor Active</span>
            </div>
        </div>

        {/* Editor Workspace */}
        <div className="flex-1 bg-gray-50/50 rounded-xl border border-dashed border-gray-200 overflow-hidden relative flex">

            {/* Sidebar (Mini) */}
            <div className="w-8 border-r border-gray-100 bg-white flex flex-col items-center py-3 gap-3">
                {[1, 2, 3].map(i => (
                    <div key={i} className={`w-5 h-4 rounded-sm border ${i === 1 ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50'}`} />
                ))}
            </div>

            {/* Main Canvas */}
            <div className="flex-1 relative flex items-center justify-center p-4">
                {/* The Slide */}
                <div className="w-full h-full bg-[#ffffff] rounded-lg shadow-xl flex flex-col p-5 relative border border-gray-100 transition-transform duration-700 hover:scale-[1.01]">
                    <div className="flex justify-between items-start mb-4">
                        <div className="space-y-1">
                            <div className="text-[9px] font-bold text-blue-600 tracking-widest uppercase mb-1">Market Opportunity</div>
                            <div className="text-xl font-extrabold text-gray-900 font-display leading-tight">Big Problem,<br />Bigger Market</div>
                        </div>
                        <div className="w-8 h-8 rounded bg-gray-50 border border-gray-100" />
                    </div>

                    <div className="flex gap-4 flex-1 items-end">
                        <div className="flex-1 space-y-2 mb-2">
                            <div className="h-2 bg-gray-100 rounded-full w-full" />
                            <div className="h-2 bg-gray-100 rounded-full w-5/6" />
                            <div className="h-2 bg-gray-100 rounded-full w-4/6" />
                        </div>
                        <div className="w-1/3 h-24 bg-blue-50/30 rounded-lg border border-blue-50 flex items-end justify-center p-2 gap-1.5">
                            <motion.div initial={{ height: '20%' }} animate={{ height: '40%' }} transition={{ delay: 0.5, duration: 1 }} className="w-3 bg-blue-300 rounded-t-sm" />
                            <motion.div initial={{ height: '40%' }} animate={{ height: '70%' }} transition={{ delay: 0.7, duration: 1 }} className="w-3 bg-blue-400 rounded-t-sm" />
                            <motion.div initial={{ height: '60%' }} animate={{ height: '100%' }} transition={{ delay: 0.9, duration: 1 }} className="w-3 bg-blue-600 rounded-t-sm shadow-sm" />
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Magic Badge - Repositioned to not overlap important content */}
            <motion.div
                initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}
                className="absolute bottom-3 right-4 flex items-center gap-2 bg-[#ffffff]/90 backdrop-blur text-gray-900 px-3 py-1.5 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 ring-1 ring-gray-50 z-10"
            >
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-500 animate-pulse shadow-lg" />
                <span className="text-[9px] font-bold tracking-wide">Polished by Deckwale</span>
            </motion.div>
        </div>
    </motion.div>
);
